import { Controller } from '@hotwired/stimulus';
import { GoogleCharts } from 'google-charts';

export default class extends Controller {
  static targets = ['line', 'highlight'];
  static values = {
    direction: String,
    currentIndex: Number,
  };

  connect() {
    GoogleCharts.load(this.drawCharts.bind(this));
  }

  targetsVisible() {
    return this.lineTarget.offsetParent !== null;
  }

  drawCharts() {
    // Target elements are placed on a tab which may not be selected yet.
    // Wait until both targets are visible
    const checkExist = setInterval(() => {
      if (this.targetsVisible()) {
        this.drawLineCharts();

        clearInterval(checkExist);
      }
    }, 100);
  }

  drawLineCharts() {
    const data = JSON.parse(this.lineTarget.dataset.values);

    const lineData = new GoogleCharts.api.visualization.DataTable();
    lineData.addColumn('string', '');
    lineData.addColumn('number', '');
    lineData.addColumn({ type: 'string', role: 'annotation' });
    lineData.addRows(data);

    // Theme-aware (dark = original light-gray axis text + dark gridlines;
    // light = navy/blue text + tan gridlines). Falls back to the original
    // dark-mode hex when no theme var is set (non-admin users).
    const axisText = this.themeColor('--color-text', '#bbb');
    const gridline = this.themeColor('--color-border', '#555');
    const baseline = this.themeColor('--color-text-muted', '#888');

    const lineOptions = {
      backgroundColor: 'transparent',
      legend: { position: 'none' },
      pointSize: 7,
      chartArea: {
        top: 10,
        width: '100%',
      },
      annotations: {
        textStyle: {
          color: this.onPrint() ? '#000' : axisText,
        },
      },
      tooltip: {
        trigger: 'none',
      },
      hAxis: {
        textStyle: {
          fontSize: 13,
          color: axisText,
        },
      },
      vAxis: {
        gridlines: {
          count: 6,
          color: gridline,
        },
        minorGridlines: {
          color: gridline,
        },
        baselineColor: baseline,
        textStyle: {
          fontSize: 13,
          color: axisText,
        },
        viewWindow: {
          min: -6,
          max: 6,
        },
      },
    };

    if (this.directionValue == 'rtl') {
      lineOptions.chartArea.right = 98;
      lineOptions.series = {
        // Axis on the right
        0: { targetAxisIndex: 1, color: this.lineTarget.dataset.color },
        1: { targetAxisIndex: 1 },
      };
    } else {
      lineOptions.chartArea.left = 95;
      lineOptions.series = {
        // Axis on the left
        0: { color: this.lineTarget.dataset.color },
      };
    }

    const lineChart = new GoogleCharts.api.visualization.LineChart(
      this.lineTarget,
    );
    lineChart.draw(lineData, lineOptions);

    // Position highlight after chart renders
    this.positionHighlight(lineChart);
  }

  positionHighlight(chart) {
    // Only show highlight if the current-index-value attribute was actually set
    // (hasCurrentIndexValue is false when no pillar is currently active)
    if (!this.hasHighlightTarget || !this.hasCurrentIndexValue) {
      return;
    }

    const chartArea = chart.getChartLayoutInterface().getChartAreaBoundingBox();
    const data = JSON.parse(this.lineTarget.dataset.values);
    const numColumns = data.length;
    const columnWidth = chartArea.width / numColumns;

    // Adjust index for RTL direction
    let displayIndex = this.currentIndexValue;
    if (this.directionValue === 'rtl') {
      displayIndex = numColumns - 1 - this.currentIndexValue;
    }

    const left = chartArea.left + displayIndex * columnWidth;

    this.highlightTarget.style.display = 'block';
    this.highlightTarget.style.left = `${left}px`;
    this.highlightTarget.style.width = `${columnWidth}px`;
    this.highlightTarget.style.top = `${chartArea.top}px`;
    this.highlightTarget.style.height = `${chartArea.height}px`;
  }

  onPrint() {
    return document
      .getElementsByTagName('body')[0]
      .classList.contains('js-print');
  }

  // Resolve a theme CSS var (defined in theme.scss) to its current value.
  themeColor(varName, fallback) {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return v || fallback;
  }
}
